import { useState, useEffect, useRef } from "react";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Sparkles, Save, RotateCcw, Image as ImageIcon, Code2, Upload, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useBranding } from "../../contexts/BrandingContext";

export default function AdminBranding() {
  const { t } = useTranslation();
  const { refresh: refreshGlobal } = useBranding();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/branding`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const onChange = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        brand_name: data.brand_name,
        brand_name_el: data.brand_name_el,
        tagline: data.tagline,
        tagline_el: data.tagline_el,
        logo_svg: data.logo_svg,
        logo_url: data.logo_url,
        use_image: !!data.use_image,
      };
      const res = await axios.put(`${API}/admin/branding`, payload);
      setData((d) => ({ ...d, ...res.data }));
      await refreshGlobal();
      toast.success(t("admin_branding:saved", { defaultValue: "Branding saved — header updated" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin_branding:save_failed", { defaultValue: "Save failed" }));
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm(t("admin_branding:confirm_reset", { defaultValue: "Reset logo and brand to TableCy defaults?" }))) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/branding/reset`);
      setData((d) => ({ ...d, ...res.data, default_logo_svg: d.default_logo_svg }));
      await refreshGlobal();
      toast.success(t("admin_branding:reset_done", { defaultValue: "Reset to defaults" }));
    } catch {
      toast.error(t("admin_branding:save_failed", { defaultValue: "Save failed" }));
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t("admin_branding:file_too_large", { defaultValue: "File must be under 2 MB" }));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Save logo_url + flip to image mode and persist immediately
      const update = { logo_url: res.data.url, use_image: true };
      setData((d) => ({ ...d, ...update }));
      await axios.put(`${API}/admin/branding`, update);
      await refreshGlobal();
      toast.success(t("admin_branding:upload_done", { defaultValue: "Logo uploaded — header updated" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin_branding:upload_failed", { defaultValue: "Upload failed" }));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleGenerateFavicon = async () => {
    if (!data?.logo_svg?.trim()) {
      toast.error(t("admin_branding:no_svg", { defaultValue: "No SVG to convert" }));
      return;
    }
    setUploading(true);
    try {
      const SIZE = 256;
      const svgBlob = new Blob([data.logo_svg.trim()], { type: "image/svg+xml;charset=utf-8" });
      const svgUrl = URL.createObjectURL(svgBlob);
      const img = new Image();
      // Wait for the SVG to be fully loaded into the Image element
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error("SVG could not be rendered to canvas"));
        img.src = svgUrl;
      });
      const canvas = document.createElement("canvas");
      canvas.width = SIZE;
      canvas.height = SIZE;
      const ctx = canvas.getContext("2d");
      // Transparent background
      ctx.clearRect(0, 0, SIZE, SIZE);
      ctx.drawImage(img, 0, 0, SIZE, SIZE);
      URL.revokeObjectURL(svgUrl);

      // Convert canvas → PNG blob → File for upload
      const pngBlob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png");
      });
      const file = new File([pngBlob], "favicon.png", { type: "image/png" });

      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Switch to image mode + use the generated PNG so the favicon picks it up
      const update = { logo_url: res.data.url, use_image: true };
      setData((d) => ({ ...d, ...update }));
      await axios.put(`${API}/admin/branding`, update);
      await refreshGlobal();
      toast.success(t("admin_branding:favicon_done", { defaultValue: "Favicon generated from SVG (256×256 PNG)" }));
    } catch (err) {
      toast.error(err.message || t("admin_branding:favicon_failed", { defaultValue: "Favicon generation failed" }));
    } finally {
      setUploading(false);
    }
  };

  if (loading || !data) {
    return <div className="animate-pulse bg-muted h-96 rounded-xl" />;
  }

  const logoToShow = data.use_image && data.logo_url ? null : data.logo_svg;

  return (
    <div data-testid="admin-branding-page" className="max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          {t("admin_branding:title", { defaultValue: "Branding" })}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t("admin_branding:subtitle", {
            defaultValue: "Customize the logo and brand name shown in the header. Bilingual support included.",
          })}
        </p>
      </div>

      {/* Live Preview */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-950 rounded-xl border border-border p-8 mb-6">
        <p className="text-xs uppercase tracking-wide text-white/50 mb-4">
          {t("admin_branding:preview", { defaultValue: "Live Preview" })}
        </p>
        <div className="flex items-center gap-3 text-white">
          {data.use_image && data.logo_url ? (
            <img src={data.logo_url} alt="logo" className="h-10 w-auto" />
          ) : (
            <span
              className="h-10 w-10 inline-flex items-center justify-center"
              aria-hidden="true"
              dangerouslySetInnerHTML={{ __html: logoToShow || "" }}
              data-testid="branding-preview-logo"
            />
          )}
          <div>
            <div className="text-xl font-semibold leading-tight">{data.brand_name || "TableCy"}</div>
            {data.tagline && <div className="text-xs text-white/60">{data.tagline}</div>}
          </div>
          <div className="border-l border-white/10 pl-4 ml-2">
            <div className="text-xl font-semibold leading-tight">{data.brand_name_el || "TableCy"}</div>
            {data.tagline_el && <div className="text-xs text-white/60">{data.tagline_el}</div>}
          </div>
        </div>
      </div>

      {/* Brand Name + Tagline */}
      <div className="bg-card rounded-xl border border-border p-5 mb-6 space-y-4">
        <h2 className="font-medium text-foreground">
          {t("admin_branding:text_section", { defaultValue: "Brand Name & Tagline" })}
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{t("admin_branding:brand_name_en", { defaultValue: "Brand Name (EN)" })}</Label>
            <Input
              value={data.brand_name || ""}
              onChange={(e) => onChange("brand_name", e.target.value)}
              className="mt-1"
              data-testid="branding-name-en"
            />
          </div>
          <div>
            <Label>{t("admin_branding:brand_name_el", { defaultValue: "Όνομα Μάρκας (EL)" })}</Label>
            <Input
              value={data.brand_name_el || ""}
              onChange={(e) => onChange("brand_name_el", e.target.value)}
              className="mt-1"
              data-testid="branding-name-el"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{t("admin_branding:tagline_en", { defaultValue: "Tagline (EN, optional)" })}</Label>
            <Input
              value={data.tagline || ""}
              onChange={(e) => onChange("tagline", e.target.value)}
              className="mt-1"
              placeholder="Cyprus Hospitality"
            />
          </div>
          <div>
            <Label>{t("admin_branding:tagline_el", { defaultValue: "Σύνθημα (EL, προαιρετικό)" })}</Label>
            <Input
              value={data.tagline_el || ""}
              onChange={(e) => onChange("tagline_el", e.target.value)}
              className="mt-1"
              placeholder="Φιλοξενία Κύπρου"
            />
          </div>
        </div>
      </div>

      {/* Logo source toggle */}
      <div className="bg-card rounded-xl border border-border p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-medium text-foreground">
              {t("admin_branding:logo_source", { defaultValue: "Logo Source" })}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              {data.use_image
                ? t("admin_branding:using_image", { defaultValue: "Using uploaded image (logo_url)" })
                : t("admin_branding:using_svg", { defaultValue: "Using inline SVG markup" })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Code2 className={`h-4 w-4 ${!data.use_image ? "text-primary" : "text-muted-foreground"}`} />
            <Switch
              checked={!!data.use_image}
              onCheckedChange={(v) => onChange("use_image", v)}
              data-testid="branding-use-image-switch"
            />
            <ImageIcon className={`h-4 w-4 ${data.use_image ? "text-primary" : "text-muted-foreground"}`} />
          </div>
        </div>

        {data.use_image ? (
          <div className="space-y-3">
            <div>
              <Label>{t("admin_branding:upload_file", { defaultValue: "Upload Logo File" })}</Label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif,.png,.jpg,.jpeg,.svg,.webp,.gif"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                  className="hidden"
                  data-testid="branding-upload-file"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="branding-upload-btn"
                >
                  {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  {uploading
                    ? t("admin_branding:uploading", { defaultValue: "Uploading..." })
                    : t("admin_branding:choose_file", { defaultValue: "Choose File" })}
                </Button>
                {data.logo_url && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 rounded-lg">
                    <img src={data.logo_url} alt="logo preview" className="h-7 w-auto" />
                    <span className="text-xs text-muted-foreground font-mono truncate max-w-xs">
                      {data.logo_url.split("/").pop()}
                    </span>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {t("admin_branding:upload_help", {
                  defaultValue: "PNG, JPG, SVG, WebP or GIF. Max 2 MB. Recommended: transparent square ~512×512 px (best for favicon).",
                })}
              </p>
            </div>

            <div className="border-t border-border pt-3">
              <Label className="text-xs text-muted-foreground">
                {t("admin_branding:or_paste_url", { defaultValue: "Or paste an external URL" })}
              </Label>
              <Input
                value={data.logo_url || ""}
                onChange={(e) => onChange("logo_url", e.target.value)}
                className="mt-1"
                placeholder="https://..."
                data-testid="branding-logo-url"
              />
            </div>
          </div>
        ) : (
          <div>
            <Label>{t("admin_branding:logo_svg", { defaultValue: "Inline SVG Markup" })}</Label>
            <Textarea
              value={data.logo_svg || ""}
              onChange={(e) => onChange("logo_svg", e.target.value)}
              className="mt-1 min-h-[180px] font-mono text-xs"
              spellCheck={false}
              data-testid="branding-logo-svg"
            />
            <p className="text-xs text-muted-foreground mt-2">
              {t("admin_branding:svg_help", {
                defaultValue: "Paste a complete <svg>…</svg>. The viewBox is preserved; the icon is rendered in a 32–40px box.",
              })}
            </p>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGenerateFavicon}
                disabled={uploading || !data.logo_svg?.trim()}
                data-testid="branding-generate-favicon-btn"
              >
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
                {uploading
                  ? t("admin_branding:generating", { defaultValue: "Generating..." })
                  : t("admin_branding:generate_favicon", { defaultValue: "Generate Favicon from SVG" })}
              </Button>
              <span className="text-xs text-muted-foreground">
                {t("admin_branding:generate_favicon_help", {
                  defaultValue: "Renders the SVG to a 256×256 PNG and uses it as the logo + favicon.",
                })}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} data-testid="branding-save-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? t("common:saving", { defaultValue: "Saving..." }) : t("admin_branding:save", { defaultValue: "Save Branding" })}
        </Button>
        <Button variant="outline" onClick={resetDefaults} disabled={saving} data-testid="branding-reset-btn">
          <RotateCcw className="h-4 w-4 mr-2" />
          {t("admin_branding:reset", { defaultValue: "Reset Defaults" })}
        </Button>
      </div>

      {data.updated_at && (
        <p className="text-xs text-muted-foreground mt-4">
          {t("admin_branding:updated_at", { defaultValue: "Last updated" })}: {new Date(data.updated_at).toLocaleString()}
          {data.updated_by ? ` · ${data.updated_by}` : ""}
        </p>
      )}
    </div>
  );
}
